import React from "react";

export interface FAQItem {
  question: string;
  answer: string;
}

export const profileFaqJsonLd: Record<string, FAQItem[]> = {
  leamary: [
    {
      question: "Léa Mary est-elle en couple ?",
      answer: "Léa Mary est connue pour préserver sa vie amoureuse des réseaux sociaux. Elle n'a pas communiqué publiquement sur sa situation sentimentale, ce qui contribue à entretenir une part de mystère autour de sa vie privée.",
    },
    {
      question: "Où vit Léa Mary ?",
      answer: "Léa Mary partage son temps entre la France et plusieurs destinations à l'étranger, notamment Marbella où elle séjourne régulièrement. Son mode de vie nomade et ses voyages font partie intégrante de son contenu.",
    },
    {
      question: "Combien Léa Mary a-t-elle d'abonnés ?",
      answer: "Léa Mary compte plusieurs millions d'abonnés cumulés sur l'ensemble de ses plateformes, Instagram, TikTok et YouTube. Elle figure dans le top 1 % des influenceuses françaises sur Instagram.",
    },
    {
      question: "Qu'est-ce que Kahee Beauty ?",
      answer: "Kahee Beauty est la marque de cosmétiques fondée par Léa Mary. Pensée pour encourager la confiance en soi et l'indépendance féminine, elle incarne le passage de Léa du statut d'influenceuse à celui d'entrepreneuse accomplie.",
    },
  ],
  capi: [
    {
      question: "Qui est Capi ?",
      answer: "Capi, de son vrai nom Timothé Medhi Kabli, est un influenceur et boxeur professionnel français. Il compte près d'un million d'abonnés sur Instagram et 1,6 million sur TikTok.",
    },
    {
      question: "Quelle est la carrière de boxe de Capi ?",
      answer: "Capi a débuté la boxe professionnelle en avril 2024 et affiche un bilan parfait de 5 victoires pour 0 défaite, dont 4 avant la limite. Il est classé dans le top 10 des poids légers français selon la Fédération Française de Boxe.",
    },
    {
      question: "Quel est le vrai nom de Capi ?",
      answer: "Le vrai nom de Capi est Timothé Medhi Kabli.",
    },
    {
      question: "Quel type de contenu Capi propose-t-il sur Twizz ?",
      answer: "Sur Twizz, Capi partage du contenu exclusif et personnel : coulisses de ses entraînements de boxe, moments de vie quotidienne, réflexions et contenus réservés à sa communauté la plus proche.",
    },
    {
      question: "Capi combat-il encore en boxe ?",
      answer: "Oui, Capi continue sa carrière de boxeur professionnel tout en maintenant sa présence sur les réseaux sociaux et Twizz.",
    },
    {
      question: "Combien coûte l'abonnement à la page Twizz de Capi ?",
      answer: "Le prix de l'abonnement varie selon les offres et promotions en cours. Rendez-vous sur sa page Twizz pour découvrir les tarifs actuels.",
    },
  ],
  carlatalon: [
    {
      question: "Carla Talon a-t-elle un enfant ?",
      answer: "Oui, Carla Talon est maman d'un petit garçon prénommé Ayden, né de sa relation avec Kevin Guedj, rencontré lors de l'émission 10 Couples Parfaits.",
    },
    {
      question: "Où vit Carla Talon ?",
      answer: "Carla Talon vit à Dubaï, aux Émirats arabes unis, où elle a posé ses valises après le succès de sa carrière digitale.",
    },
    {
      question: "Carla Talon est-elle ambassadrice de marques ?",
      answer: "Oui, Carla Talon est notamment ambassadrice de FashionNova, la marque de mode internationale avec laquelle elle collabore régulièrement et dont elle met en avant les collections auprès de sa communauté.",
    },
  ],
  melpopss: [
    {
      question: "Quel est le vrai prénom de Mel Popss ?",
      answer: "Mel Popss est le pseudonyme de Mélany, une influenceuse d'origine portugaise et brésilienne basée à Paris.",
    },
    {
      question: "Quelle est l'origine de Mel Popss ?",
      answer: "Mel Popss est d'origine portugaise et brésilienne. Ce double héritage culturel se reflète dans son style unique et son tempérament solaire qui séduisent sa communauté.",
    },
    {
      question: "Mel Popss a-t-elle participé à des clips musicaux ?",
      answer: "Oui, Mel Popss s'est fait remarquer en apparaissant dans plusieurs clips de rappeurs français de premier plan, notamment Booba, Niska et Koba LaD. Ces collaborations ont largement contribué à asseoir sa notoriété au-delà des réseaux sociaux.",
    },
    {
      question: "Mel Popss est-elle ambassadrice de marques ?",
      answer: "Oui, Mel Popss est ambassadrice de FashionNova, la marque de mode internationale. Elle collabore régulièrement avec des marques de mode et de beauté qu'elle met en avant auprès de sa communauté.",
    },
    {
      question: "Depuis quand Mel Popss est-elle active sur Instagram ?",
      answer: "Mel Popss a rejoint Instagram dès janvier 2014. Elle y a construit patiemment une communauté de plus de 235 000 abonnés grâce à ses looks de mode innovants et son authenticité.",
    },
  ],
};

export const getProfileFaqJsonLd = (username: string): string | null => {
  const faqData = profileFaqJsonLd[username];
  if (!faqData || faqData.length === 0) {
    return null;
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return JSON.stringify(jsonLd);
};

export const profileSeoContents: Record<string, React.ReactNode> = {
  leamary: (
    <>
      <p>
        Connue de millions de personnes sur Instagram, TikTok et YouTube, Léa Mary s'est imposée comme l'une des créatrices de contenu les plus
        influentes de France. Avec un style immédiatement reconnaissable, une authenticité rare et une communauté fidèle et engagée, elle a su
        construire bien plus qu'une simple audience : une véritable famille digitale. Sur Twizz, Léa franchit un cap supplémentaire en proposant à ses
        abonnés les plus proches un accès privilégié à son univers, loin des filtres des réseaux sociaux classiques.
      </p>
      <h2>Qui est Léa Mary ?</h2>
      <p>
        Née le 23 janvier 1998, Léa Mary a débuté sa carrière en ligne sur Snapchat avant de conquérir rapidement Instagram, puis TikTok et YouTube.
        Son contenu, centré sur la mode, la beauté, le fitness et l'entrepreneuriat féminin, a touché des millions de femmes en quête d'inspiration et
        d'authenticité. Ce qui distingue Léa des autres créatrices, c'est sa capacité à allier élégance et proximité : elle partage aussi bien ses
        tenues de luxe que les coulisses de sa vie quotidienne, avec une transparence qui fait toute sa force.
      </p>
      <p>
        Classée dans le top 1 % des influenceuses Instagram en France, Léa Mary est bien plus qu'un visage sur un écran. C'est une entrepreneuse
        accomplie, fondatrice de Kahee Beauty, sa propre marque de cosmétiques pensée pour encourager l'indépendance et la confiance en soi des
        femmes. Son parcours, de simple utilisatrice des réseaux à femme d'affaires reconnue, est une source de motivation pour toute une génération.
      </p>
      <h2>Pourquoi rejoindre Léa Mary sur Twizz ?</h2>
      <p>
        Twizz est la plateforme qui permet à Léa Mary de partager ce qu'elle ne publie pas ailleurs. Loin des algorithmes contraignants d'Instagram ou
        de TikTok, elle y exprime librement sa personnalité, son quotidien et ses centres d'intérêt avec ceux qui la soutiennent vraiment. En
        rejoignant sa page Twizz, vous entrez dans un cercle restreint où la relation entre Léa et sa communauté est directe, intime et sans
        intermédiaire.
      </p>
      <p>
        C'est l'opportunité unique de découvrir une version plus personnelle de Léa : ses réflexions, ses projets en coulisses, ses passions, et bien
        plus encore. Pour ses fans les plus fidèles, c'est la chance de vivre une expérience différente, plus authentique, avec une créatrice qui a
        toujours placé sa communauté au coeur de tout ce qu'elle fait.
      </p>
      <h2>Léa Mary, une influenceuse aux multiples facettes</h2>
      <p>
        Ce qui rend Léa Mary si captivante, c'est la richesse de son univers. Mode haut de gamme, conseils beauté, voyages, fitness, entrepreneuriat
        féminin... son contenu couvre un spectre large tout en restant profondément cohérent avec ses valeurs. Son séjour à Marbella, ses
        collaborations avec des marques internationales comme FashionNova, ou encore le lancement de Kahee Beauty sont autant de chapitres d'une
        histoire inspirante qu'elle continue d'écrire chaque jour. Sur Twizz, cette richesse prend une nouvelle dimension. Les abonnés ont accès à des
        contenus que Léa choisit de partager uniquement avec eux, dans un espace sécurisé et bienveillant, loin du bruit des réseaux publics.
      </p>
      <h3>Mode et beauté : l'ADN de Léa Mary</h3>
      <p>
        Depuis ses débuts, la mode et la beauté sont au coeur de l'identité de Léa. Son sens du style, mélange de pièces luxe et de looks accessibles,
        en a fait une référence pour des milliers de femmes. Sur Twizz, elle partage cette passion de manière encore plus libre et spontanée.
      </p>
      <h3>Kahee Beauty : quand l'influence devient entrepreneuriat</h3>
      <p>
        Fondatrice de sa propre marque de cosmétiques, Léa Mary incarne une nouvelle génération d'influenceuses qui transforment leur notoriété en
        véritable projet d'entreprise. Kahee Beauty, c'est son engagement concret pour l'autonomisation des femmes.
      </p>
      <h3>Rejoignez la communauté Twizz de Léa Mary</h3>
      <p>
        Que vous la suiviez depuis ses débuts ou que vous la découvriez aujourd'hui, la page Twizz de Léa Mary est l'endroit idéal pour aller plus
        loin dans cette relation unique entre une créatrice et sa communauté. Abonnez-vous dès maintenant et rejoignez les milliers de personnes qui
        font déjà partie de son cercle exclusif.
      </p>
      <h2>FAQ</h2>
      <h3>Léa Mary est-elle en couple ?</h3>
      <p>
        Léa Mary est connue pour préserver sa vie amoureuse des réseaux sociaux. Elle n'a pas communiqué publiquement sur sa situation sentimentale,
        ce qui contribue à entretenir une part de mystère autour de sa vie privée.
      </p>
      <h3>Où vit Léa Mary ?</h3>
      <p>
        Léa Mary partage son temps entre la France et plusieurs destinations à l'étranger, notamment Marbella où elle séjourne régulièrement. Son mode
        de vie nomade et ses voyages font partie intégrante de son contenu.
      </p>
      <h3>Combien Léa Mary a-t-elle d'abonnés ?</h3>
      <p>
        Léa Mary compte plusieurs millions d'abonnés cumulés sur l'ensemble de ses plateformes, Instagram, TikTok et YouTube. Elle figure dans le top
        1 % des influenceuses françaises sur Instagram.
      </p>
      <h3>Qu'est-ce que Kahee Beauty ?</h3>
      <p>
        Kahee Beauty est la marque de cosmétiques fondée par Léa Mary. Pensée pour encourager la confiance en soi et l'indépendance féminine, elle
        incarne le passage de Léa du statut d'influenceuse à celui d'entrepreneuse accomplie.
      </p>
    </>
  ),

  capi: (
    <>
      <p>
        Influenceur, boxeur professionnel invaincu, personnalité clivante et authentique : Capi, de son vrai nom Timothé Medhi Kabli, est l'une des
        figures masculines les plus suivies de la scène digitale française. Avec une communauté de près d'un million d'abonnés sur Instagram et 1,6
        million sur TikTok, il a su transformer sa notoriété en ligne en un véritable projet de vie, porté par une ambition sans limite et une
        capacité rare à fédérer autour de lui. Sur Twizz, il ouvre les portes d'un espace encore plus exclusif et personnel pour ceux qui veulent le
        suivre au plus près.
      </p>

      <h2>Qui est Capi ?</h2>
      <p>
        Connu sur les réseaux sociaux sous le pseudonyme Capi, Timothé Medhi Kabli s'est imposé comme une figure incontournable de la scène digitale
        française avant de surprendre tout le monde en se lançant dans la boxe professionnelle en avril 2024. Loin de l'image de l'influenceur qui
        monte sur un ring pour le buzz, Capi a construit une carrière pugilistique sérieuse et rigoureuse, affichant un bilan parfait de 5 victoires
        pour 0 défaite, dont 4 avant la limite. Un ratio de finisseur impressionnant qui lui a valu d'être classé parmi les meilleurs poids légers
        français selon la Fédération Française de Boxe.
      </p>
      <p>
        Avant la boxe, c'est sur TikTok et Instagram qu'il a construit sa communauté, grâce à un contenu direct, sans filtre et toujours authentique.
        Son style, entre proximité et authenticité, a fédéré des millions de personnes qui le suivent autant pour sa personnalité que pour ses
        exploits sportifs.
      </p>

      <h2>Pourquoi rejoindre Capi sur Twizz ?</h2>
      <p>
        Sur Twizz, Capi partage ce qu'il réserve à ses abonnés les plus proches. Loin des contraintes des réseaux sociaux classiques et de leurs
        algorithmes, il y propose un contenu plus personnel, plus brut et plus direct. En rejoignant sa page, vous intégrez un cercle privilégié où la
        relation entre Capi et sa communauté prend une autre dimension.
      </p>
      <p>
        C'est ici qu'il dévoile les coulisses de sa vie de boxeur, ses entraînements, ses réflexions, ses projets et des moments authentiques que l'on
        ne retrouve nulle part ailleurs. Pour ses fans les plus fidèles, c'est l'opportunité unique de vivre de l'intérieur le quotidien d'un homme
        qui repousse ses limites chaque jour.
      </p>

      <h2>Capi, l'influenceur qui est passé à l'action</h2>
      <p>
        Ce qui distingue Capi de la plupart des créateurs de contenu, c'est qu'il ne se contente pas de parler : il agit. Là où beaucoup
        d'influenceurs restent cantonnés aux réseaux sociaux, il a choisi de prouver sa valeur sur un ring, face à des boxeurs professionnels, sous
        les yeux du grand public. Une démarche rare et respectée, y compris dans le milieu pugilistique.
      </p>

      <h3>Un palmarès de boxe qui force le respect</h3>
      <p>
        Depuis ses débuts professionnels en avril 2024, Capi n'a jamais perdu. Cinq combats, cinq victoires, quatre KO. Il est classé dans le top 10
        des poids légers français par la Fédération Française de Boxe, une reconnaissance concrète qui dépasse largement le cadre de l'influence.
      </p>

      <h3>Une communauté digitale massive et engagée</h3>
      <p>
        Avec près d'un million d'abonnés sur Instagram et 1,6 million sur TikTok, Capi dispose d'une des communautés masculines les plus engagées de
        France. Son contenu direct et sans concession attire une audience fidèle qui le suit autant pour son authenticité que pour son parcours
        sportif hors normes.
      </p>

      <h3>Rejoignez la communauté Twizz de Capi</h3>
      <p>
        Que vous le suiviez depuis ses débuts sur les réseaux ou que vous l'ayez découvert via la boxe, la page Twizz de Capi est l'endroit idéal pour
        aller plus loin dans cette relation unique. Abonnez-vous dès maintenant et rejoignez ceux qui font déjà partie de son cercle le plus exclusif.
      </p>

      <h2>FAQ</h2>
      <h3>Qui est Capi ?</h3>
      <p>
        Capi, de son vrai nom Timothé Medhi Kabli, est un influenceur et boxeur professionnel français. Il compte près d'un million d'abonnés sur
        Instagram et 1,6 million sur TikTok, et affiche un bilan parfait de 5 victoires pour 0 défaite depuis ses débuts professionnels en avril 2024.
      </p>

      <h3>Capi est-il vraiment boxeur professionnel ?</h3>
      <p>
        Oui, Capi est licencié et classé par la Fédération Française de Boxe. Il figure dans le top 10 des poids légers français et a disputé
        plusieurs combats à l'étranger, notamment à Dubaï, prouvant que sa carrière de boxeur va bien au-delà d'une simple opération marketing.
      </p>

      <h3>Quel est le vrai nom de Capi ?</h3>
      <p>
        Son vrai nom est Timothé Medhi Kabli. Il est connu en ligne sous le pseudonyme Capi, ou encore sous les pseudos capi_cs sur TikTok et capi.cs3
        sur Instagram.
      </p>

      <h3>Quel type de contenu Capi partage-t-il sur Twizz ?</h3>
      <p>
        Sur Twizz, Capi partage un contenu exclusif réservé à ses abonnés les plus proches : coulisses de ses entraînements, moments personnels,
        réflexions et publications qu'il ne diffuse pas sur ses autres réseaux sociaux.
      </p>

      <h3>Où Capi a-t-il disputé ses combats de boxe ?</h3>
      <p>
        Plusieurs de ses combats professionnels se sont déroulés à Dubaï, aux Émirats arabes unis. Son 5e combat a eu lieu le 28 août 2025 à
        Enghien-les-Bains, en France, où il a remporté une victoire aux points à l'unanimité.
      </p>

      <h3>Twizz est-il gratuit pour accéder au contenu de Capi ?</h3>
      <p>
        Twizz fonctionne sur un modèle d'abonnement. Une partie du contenu de Capi est accessible librement, tandis que ses publications les plus
        exclusives sont réservées aux abonnés payants de sa page.
      </p>
    </>
  ),

  carlatalon: (
    <>
      <p>
        Révélée au grand public par la téléréalité française, Carla Talon s'est imposée bien au-delà des écrans comme une figure incontournable du
        monde de l'influence. Franco-italienne, née le 12 mars 1996, elle allie charisme naturel, sens aigu du style et authenticité pour offrir à sa
        communauté un contenu toujours soigné et captivant. Sur Twizz, elle va encore plus loin en ouvrant les portes de son univers le plus exclusif
        à ceux qui souhaitent la suivre de plus près.
      </p>

      <h2>Qui est Carla Talon ?</h2>
      <p>
        Originaire de Nice, Carla Talon a grandi entre la culture française de son père et l'élégance italienne de sa mère, un héritage qui se ressent
        dans chacune de ses publications. C'est en 2020 qu'elle se fait remarquer du grand public en intégrant le casting de 10 Couples Parfaits sur
        TFX. Sa personnalité solaire et son naturel face caméra lui valent rapidement une base de fans solide et fidèle. Elle enchaîne ensuite avec
        des participations dans Les Vacances des Anges 4 et La Villa 7, confirmant sa place dans le paysage de la téléréalité française.
      </p>
      <p>
        Mais Carla ne se limite pas à la télévision. Très active sur Instagram où elle rassemble plus de 425 000 abonnés, elle y partage son quotidien
        glamour, ses tenues colorées et sophistiquées, ses voyages et ses collaborations avec des marques de mode et de beauté. Sa capacité à rester
        proche de sa communauté, tout en cultivant une image élégante et raffinée, fait d'elle une influenceuse à part dans son univers.
      </p>

      <h2>Pourquoi rejoindre Carla Talon sur Twizz ?</h2>
      <p>
        Sur Twizz, Carla Talon propose un espace intime et sécurisé, loin des contraintes des réseaux sociaux traditionnels. C'est ici qu'elle partage
        ce qu'elle réserve à ses abonnés les plus proches : des contenus plus personnels, des coulisses de sa vie, des moments authentiques que l'on
        ne retrouve pas ailleurs. La relation entre Carla et sa communauté prend une toute autre dimension sur cette plateforme, plus directe, plus
        sincère et plus exclusive.
      </p>
      <p>
        En rejoignant sa page Twizz, vous accédez à un cercle privilégié où Carla s'exprime librement, sans filtre imposé par un algorithme. C'est
        l'occasion unique d'aller au-delà de ce qu'elle montre sur Instagram ou TikTok et de vivre une expérience plus immersive avec elle.
      </p>

      <h2>Carla Talon, bien plus qu'une influenceuse</h2>
      <p>
        Ce qui distingue Carla Talon, c'est sa capacité à se réinventer en permanence. De la téléréalité à l'influence digitale, elle a su transformer
        chaque étape de son parcours en opportunité, consolidant une image forte et cohérente. Son style, qui mêle élégance italienne et audace
        française, lui permet de se démarquer dans un univers très concurrentiel.
      </p>

      <h3>Un univers mode et lifestyle assumé</h3>
      <p>
        Carla Talon est reconnue pour son goût prononcé pour la mode et le raffinement. Ses publications, toujours soignées, mettent en valeur des
        tenues colorées et sophistiquées qui inspirent ses abonnées. Elle a notamment collaboré avec FashionNova, dont elle est ambassadrice,
        apportant sa touche personnelle à chaque mise en avant.
      </p>

      <h3>Une personnalité authentique et proche de sa communauté</h3>
      <p>
        Ce qui fidélise autant ses abonnés, c'est sa proximité. Carla n'hésite pas à partager les moments simples de son quotidien, à répondre à sa
        communauté et à s'exprimer avec spontanéité. Cette authenticité, rare dans le milieu de l'influence, est au coeur de sa relation avec son
        public.
      </p>

      <h2>FAQ</h2>
      <h3>Carla Talon est-elle en couple ?</h3>
      <p>
        Carla Talon est en couple avec Bryan Dubois. Le 9 mars 2025, elle a annoncé sa grossesse, et le 23 juillet 2025, elle a donné naissance à leur
        fille Amaya.
      </p>

      <h3>Où vit Carla Talon ?</h3>
      <p>Carla Talon vit à Dubaï, aux Émirats arabes unis, où elle a posé ses valises après le succès de sa carrière digitale.</p>

      <h3>Carla Talon est-elle ambassadrice de marques ?</h3>
      <p>
        Oui, Carla Talon est notamment ambassadrice de FashionNova, la marque de mode internationale avec laquelle elle collabore régulièrement et
        dont elle met en avant les collections auprès de sa communauté.
      </p>
    </>
  ),

  melpopss: (
    <>
      <p>
        Reconnaissable entre mille avec son style audacieux et son charisme naturel, Mel Popss s'est imposée comme une figure incontournable de
        l'influence française. D'origine portugaise et brésilienne, basée à Paris, elle a su construire une communauté fidèle et engagée grâce à une
        personnalité authentique et une créativité sans limite. Sur Twizz, elle ouvre les portes d'un espace encore plus intime et exclusif, réservé à
        ceux qui souhaitent la suivre au plus près.
      </p>

      <h2>Qui est Mel Popss ?</h2>
      <p>
        De son vrai prénom Mélany, Mel Popss a débuté sa présence en ligne sur Instagram dès 2014, construisant pas à pas une audience de plus de 235
        000 abonnés attirés par ses looks de mode innovants, ses conseils beauté et les coulisses de son quotidien glamour. Son image, immédiatement
        reconnaissable avec ses cheveux rouges emblématiques, traduit une personnalité assumée et une liberté de ton qui tranche dans un univers
        souvent très lissé.
      </p>
      <p>
        Elle s'est également fait un nom dans le milieu du clip musical français, en apparaissant aux côtés de rappeurs de premier plan comme Booba,
        Niska et Koba LaD. Ces collaborations lui ont apporté une visibilité supplémentaire et ont renforcé son image d'icône urbaine et tendance.
        Ambassadrice FashionNova, elle influence les tendances mode de sa communauté au quotidien, entre pièces luxe et looks streetwear assumés.
      </p>

      <h2>Pourquoi rejoindre Mel Popss sur Twizz ?</h2>
      <p>
        Twizz est la plateforme où Mel Popss partage ce qu'elle réserve à ses abonnés les plus proches. Loin des algorithmes des réseaux sociaux
        classiques, elle y propose un contenu plus personnel, plus libre et plus direct. En rejoignant sa page, vous intégrez un cercle privilégié où
        la relation entre Mel et sa communauté prend une toute autre dimension.
      </p>
      <p>
        C'est ici qu'elle dévoile les coulisses de sa vie d'influenceuse, des moments authentiques de son quotidien et des contenus exclusifs que l'on
        ne retrouve nulle part ailleurs. Pour ses fans les plus fidèles, c'est l'opportunité unique de vivre une expérience immersive avec une
        créatrice qui a toujours placé la connexion avec son public au coeur de son travail.
      </p>

      <h2>Mel Popss, une créatrice au style unique</h2>
      <p>
        Ce qui distingue Mel Popss dans l'univers de l'influence, c'est l'originalité de son identité visuelle et la cohérence de son univers. Chaque
        publication est pensée pour refléter sa personnalité : audacieuse, créative et toujours authentique.
      </p>

      <h3>Un style mode assumé et inspirant</h3>
      <p>
        Mel Popss est une référence mode pour sa communauté. Son sens du style, qui mêle influences urbaines et touches glamour, en fait une source
        d'inspiration quotidienne pour des milliers de femmes. Ses collaborations avec des marques internationales comme FashionNova témoignent de sa
        crédibilité dans l'univers de la mode.
      </p>

      <h3>Une présence digitale forte et engagée</h3>
      <p>
        Sur Instagram, TikTok et désormais Twizz, Mel Popss entretient un lien fort avec sa communauté. Elle prend le temps de répondre à ses abonnés,
        de partager des moments personnels et de créer un espace d'échange sincère et bienveillant. Cette proximité est au coeur de ce qui fait son
        succès.
      </p>

      <h3>Rejoignez la communauté Twizz de Mel Popss</h3>
      <p>
        Que vous la suiviez depuis ses débuts ou que vous la découvriez aujourd'hui, la page Twizz de Mel Popss est l'endroit idéal pour aller plus
        loin dans cette relation unique. Abonnez-vous dès maintenant et rejoignez ceux qui font déjà partie de son cercle exclusif.
      </p>

      <h2>FAQ</h2>
      <h3>Quel est le vrai prénom de Mel Popss ?</h3>
      <p>Mel Popss est le pseudonyme de Mélany, une influenceuse d'origine portugaise et brésilienne basée à Paris.</p>

      <h3>Quelle est l'origine de Mel Popss ?</h3>
      <p>
        Mel Popss est d'origine portugaise et brésilienne. Ce double héritage culturel se reflète dans son style unique et son tempérament solaire qui
        séduisent sa communauté.
      </p>

      <h3>Mel Popss a-t-elle participé à des clips musicaux ?</h3>
      <p>
        Oui, Mel Popss s'est fait remarquer en apparaissant dans plusieurs clips de rappeurs français de premier plan, notamment Booba, Niska et Koba
        LaD. Ces collaborations ont largement contribué à asseoir sa notoriété au-delà des réseaux sociaux.
      </p>

      <h3>Mel Popss est-elle ambassadrice de marques ?</h3>
      <p>
        Oui, Mel Popss est ambassadrice de FashionNova, la marque de mode internationale. Elle collabore régulièrement avec des marques de mode et de
        beauté qu'elle met en avant auprès de sa communauté.
      </p>

      <h3>Depuis quand Mel Popss est-elle active sur Instagram ?</h3>
      <p>
        Mel Popss a rejoint Instagram dès janvier 2014. Elle y a construit patiemment une communauté de plus de 235 000 abonnés grâce à ses looks de
        mode innovants et son authenticité.
      </p>
    </>
  ),
};

export const getProfileSeoContent = (username: string): React.ReactNode | null => {
  return profileSeoContents[username.toLowerCase()] || null;
};
